import { drawExtensionIcon } from '@/extensions/pixelIcons';
import type { NodeHandle } from '@/models/logic/RuntimeHandles';
import { z } from 'zod';
import { createTimeChip } from './chip';
import { reaction } from 'mobx';
import type { DigitilesExtension } from '../catalog';
import { watchActiveExtensionNodes, type ActiveExtensionNode } from '../activeNodes';
import { configureTile } from '../configuration';
import { InputType, type TileDefinition } from '@/models/tiles/TileDefinition';
import { standardBuildAndConnect } from '@/models/logic/utils';
import { CircuitType, Side } from '@/schemas/common';
import { TileSideIOType } from '@/schemas/tiles';
import {
  defaultTileGraphics,
  connectionGraphics,
} from '@/models/tiles/DefaultTileGraphics';
import { colors } from '@/styles';

export const TIME_TYPE = 'extension:digitiles.time/seconds' as const;
export const TIME_BITS = 48;
const configSchema = z.object({
  bit: z
    .number()
    .int()
    .min(0)
    .max(TIME_BITS - 1),
});
const runtimeSchema = configSchema.extend({ enabled: z.boolean(), value: z.boolean() });
type Runtime = z.infer<typeof runtimeSchema>;
// Arithmetic avoids JavaScript's signed 32-bit bitwise coercion (2038/2106).
export function unixSeconds(milliseconds: number): number {
  if (!Number.isFinite(milliseconds)) return 0;
  return Math.max(0, Math.min(2 ** TIME_BITS - 1, Math.floor(milliseconds / 1000)));
}
export const secondsBit = (seconds: number, bit: number) =>
  Math.floor(seconds / 2 ** bit) % 2 === 1;
const none = { type: TileSideIOType.NONE, forceConnection: false };
const ioSides = {
  [Side.TOP]: none,
  [Side.RIGHT]: { type: TileSideIOType.OUTPUT, forceConnection: true },
  [Side.BOTTOM]: none,
  [Side.LEFT]: none,
};
const tile: TileDefinition<z.infer<typeof configSchema>> = {
  type: TIME_TYPE,
  name: 'Unix seconds bit',
  canRotate: true,
  stateSchema: configSchema,
  createDefaultState: () => ({ bit: 0 }),
  graphConfiguration: (state) => ({ bit: state.bit }),
  ioSides,
  buildAndConnectNodes: (data) => {
    const queue = standardBuildAndConnect(TIME_TYPE, data, tile);
    const node = data.graph.nodes.get(data.instance.logicNodeIds!.right!);
    if (node) node.state.bit = data.instance.state.bit;
    return queue;
  },
  createContextMenuItems: ({ instance, chipEditorModel }) => [
    {
      label: `Timestamp bit (${instance.state.bit})`,
      action: async (takeInput) => {
        try {
          const bit = await takeInput(
            InputType.NUMBER,
            'Unix seconds bit (0–47; 0 is the low bit)',
            instance.state.bit,
            true,
            (value) => ({
              isValid: configSchema.shape.bit.safeParse(Number(value)).success,
              errorMessage: 'Enter a whole number from 0 to 47.',
            }),
          );
          configureTile(
            instance,
            chipEditorModel,
            { bit: configSchema.shape.bit.parse(Number(bit)) },
            'Change timestamp bit',
          );
        } catch {
          /* Cancel preserves configuration. */
        }
      },
    },
  ],
  graphics: {
    type: TIME_TYPE,
    draw(g, { meta, connections, context }) {
      defaultTileGraphics(g);
      connectionGraphics(
        g,
        colors.signalColor,
        colors.noSignalColor,
        meta,
        connections,
        ioSides,
        context,
      );
      drawExtensionIcon(
        g,
        'clock',
        meta.outputs.right ? colors.signalColor : colors.noSignalColor,
      );
    },
  },
};

/** Inject wall time for repeatable tests; process() never reads the clock. */
export function createTimeExtension(now: () => number = Date.now): DigitilesExtension {
  return {
    id: 'digitiles.time',
    name: 'System time',
    version: '1.0.0',
    description:
      'Read UTC Unix seconds as 48 unsigned bits (0–47). All bits share one sample, refreshed every second. Pausing holds the sample; resuming reads current time. Enable live input to read the system clock.',
    capabilities: ['time'],
    chips: [{ name: '48-bit System time chip', bundle: createTimeChip(TIME_TYPE) }],
    tiles: [{ definition: tile, circuits: [CircuitType.ChipLogic] }],
    nodes: [
      {
        type: TIME_TYPE,
        stateSchema: runtimeSchema.transform((state) => ({
          ...state,
          enabled: false,
          value: false,
        })),
        configurationKeys: ['bit'],
        createDefaultState: (): Runtime => ({ bit: 0, enabled: false, value: false }),
        process: (_input, state: Runtime) => state.enabled && state.value,
      },
    ],
    activate(app) {
      let active: ActiveExtensionNode[] = [];
      let stopRunning = () => {};
      const publish = (
        targets: ActiveExtensionNode[],
        seconds: number,
        enabled: boolean,
      ) => {
        const changed = new Map<ActiveExtensionNode['simulation'], NodeHandle[]>();
        // Assign every bit before any simulator can observe the sample.
        for (const target of targets) {
          if (enabled && !target.simulation.isRunning) continue;
          const state = target.node.state as Runtime;
          const value = enabled && secondsBit(seconds, state.bit);
          if (state.enabled === enabled && state.value === value) continue;
          state.enabled = enabled;
          state.value = value;
          if (target.simulation.graph.nodes.get(target.node.id) !== target.node) continue;
          let ids = changed.get(target.simulation);
          if (!ids) changed.set(target.simulation, (ids = []));
          ids.push(target.node.id);
        }
        for (const [simulation, ids] of changed) {
          for (const id of ids) simulation.dirtyNodes.add(id);
          simulation.triggerUpdate(ids[0]);
        }
      };
      const sample = () => publish(active, unixSeconds(now()), true);
      const unwatch = watchActiveExtensionNodes(app, TIME_TYPE, (nodes) => {
        stopRunning();
        const retained = new Set(nodes.map((target) => target.node));
        publish(
          active.filter((target) => !retained.has(target.node)),
          0,
          false,
        );
        active = nodes;
        stopRunning = reaction(
          () =>
            [...new Set(active.map((target) => target.simulation))].map(
              (simulation) => simulation.isRunning,
            ),
          sample,
        );
        if (active.length) sample();
      });
      const timer = setInterval(() => {
        if (active.some((target) => target.simulation.isRunning)) sample();
      }, 1000);
      return () => {
        clearInterval(timer);
        unwatch();
        stopRunning();
      };
    },
  };
}
export const timeExtension = createTimeExtension();
